import { API_BASE } from '../config/api';
import { optimizeImageFile } from '../utils/imageOptimization';
import { buildUrl, contextAwareHeaders } from './apiClient';


const createRequestUrl = (path) => {
  const resolvedUrl = buildUrl(path);

  if (/^https?:\/\//i.test(resolvedUrl)) {
    return new URL(resolvedUrl);
  }

  const origin = typeof window !== 'undefined' && window.location?.origin ? window.location.origin : 'http://localhost';
  return new URL(resolvedUrl, origin);
};

const handleResponse = async (response, fallbackMessage = 'Unable to process request.') => {
  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    const message = data.error || data.message || fallbackMessage;
    const error = new Error(message);
    error.status = response.status;
    error.data = data;
    throw error;
  }

  return data;
};

const normalizeId = (value) => {
  if (!value) return '';

  if (typeof value === 'object') {
    return value.id || value._id || '';
  }

  return value;
};

const extractTournamentItems = (payload) => {
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.tournaments)) return payload.tournaments;
  if (Array.isArray(payload?.data?.items)) return payload.data.items;
  if (Array.isArray(payload?.data?.tournaments)) return payload.data.tournaments;
  return [];
};


export const fetchTournaments = async (token, _organizationId, filters = {}) => {
  const {
    tournamentId,
    view = 'all',
    q = '',
    page = 1,
    limit = 100,
    organizationId,
    communityId,
    clubId,
  } = filters || {};

  const headers = contextAwareHeaders({ Authorization: `Bearer ${token}` });

  const buildMineUrl = (requestedView) => {
    const url = createRequestUrl('/api/tournaments/my');

    if (tournamentId !== undefined && tournamentId !== null && `${tournamentId}`.trim() !== '') {
      url.searchParams.set('tournamentId', tournamentId);
    }

    if (requestedView) {
      url.searchParams.set('view', requestedView);
    }

    if (q) {
      url.searchParams.set('q', q);
    }

    if (page) {
      url.searchParams.set('page', String(page));
    }

    if (limit) {
      url.searchParams.set('limit', String(limit));
    }

    const resolvedOrganizationId = normalizeId(organizationId);
    if (resolvedOrganizationId) {
      url.searchParams.set('organizationId', resolvedOrganizationId);
    }

    const resolvedCommunityId = normalizeId(communityId);
    if (resolvedCommunityId) {
      url.searchParams.set('communityId', resolvedCommunityId);
    }

    const resolvedClubId = normalizeId(clubId);
    if (resolvedClubId) {
      url.searchParams.set('clubId', resolvedClubId);
    }

    return url;
  };

  if (view === 'all') {
    const [activeResponse, archivedResponse] = await Promise.all([
      fetch(buildMineUrl('active').toString(), { headers }),
      fetch(buildMineUrl('archived').toString(), { headers }),
    ]);

    const [activeData, archivedData] = await Promise.all([
      handleResponse(activeResponse, 'Unable to load tournaments.'),
      handleResponse(archivedResponse, 'Unable to load tournaments.'),
    ]);

    const activeItems = extractTournamentItems(activeData);
    const archivedItems = extractTournamentItems(archivedData);

    return {
      items: [...activeItems, ...archivedItems],
      tournaments: [...activeItems, ...archivedItems],
      page,
      limit,
      total: Number(activeData?.total || 0) + Number(archivedData?.total || 0),
    };
  }

  const response = await fetch(buildMineUrl(view).toString(), { headers });

  const data = await handleResponse(response, 'Unable to load tournaments.');
  const normalizedItems = extractTournamentItems(data);

  if (normalizedItems.length > 0) {
    return {
      ...data,
      items: normalizedItems,
      tournaments: normalizedItems,
    };
  }

  return data;
};

export const fetchTournamentAdmins = async (token, organizationId, options = {}) => {
  const url = createRequestUrl('/api/tournaments/admins');

  const resolvedOrganizationId = normalizeId(organizationId);
  if (resolvedOrganizationId) {
    url.searchParams.set('organizationId', resolvedOrganizationId);
  }
  const resolvedCommunityId = normalizeId(options?.communityId);
  if (resolvedCommunityId) {
    url.searchParams.set('communityId', resolvedCommunityId);
  }
  const resolvedClubId = normalizeId(options?.clubId);
  if (resolvedClubId) {
    url.searchParams.set('clubId', resolvedClubId);
  }
  if (options.includeUsers) {
    url.searchParams.set('includeUsers', 'true');
  }

  const response = await fetch(url, {
    headers: contextAwareHeaders({ Authorization: `Bearer ${token}` }),
  });

  return handleResponse(response, 'Unable to load tournament admins.');
};

export const fetchTournamentById = async (tournamentId, token, organizationId, options = {}) => {
  const url = createRequestUrl(`/api/tournaments/${tournamentId}`);

  const resolvedOrganizationId = normalizeId(organizationId);
  if (resolvedOrganizationId) {
    url.searchParams.set('organizationId', resolvedOrganizationId);
  }
  const resolvedCommunityId = normalizeId(options?.communityId);
  if (resolvedCommunityId) {
    url.searchParams.set('communityId', resolvedCommunityId);
  }
  const resolvedClubId = normalizeId(options?.clubId);
  if (resolvedClubId) {
    url.searchParams.set('clubId', resolvedClubId);
  }

  const response = await fetch(url, {
    headers: contextAwareHeaders({ Authorization: `Bearer ${token}` }),
  });

  return handleResponse(response, 'Unable to load tournament.');
};

export const fetchTournamentRegisteredPlayers = async (
  tournamentId,
  token,
  organizationId,
  options = {}
) => {
  const url = createRequestUrl(`/api/tournaments/${tournamentId}/registered-players`);

  const resolvedOrganizationId = normalizeId(organizationId);
  if (resolvedOrganizationId) {
    url.searchParams.set('organizationId', resolvedOrganizationId);
  }
  const resolvedCommunityId = normalizeId(options?.communityId);
  if (resolvedCommunityId) {
    url.searchParams.set('communityId', resolvedCommunityId);
  }
  const resolvedClubId = normalizeId(options?.clubId);
  if (resolvedClubId) {
    url.searchParams.set('clubId', resolvedClubId);
  }

  const response = await fetch(url, {
    headers: contextAwareHeaders({ Authorization: `Bearer ${token}` }),
  });

  return handleResponse(response, 'Unable to load tournament players.');
};

export const fetchTournamentMatches = async (tournamentId, token, organizationId, options = {}) => {
  const url = createRequestUrl(`/api/tournaments/${tournamentId}/matches`);

  const resolvedOrganizationId = normalizeId(organizationId);
  if (resolvedOrganizationId) {
    url.searchParams.set('organizationId', resolvedOrganizationId);
  }
  const resolvedCommunityId = normalizeId(options?.communityId);
  if (resolvedCommunityId) {
    url.searchParams.set('communityId', resolvedCommunityId);
  }
  const resolvedClubId = normalizeId(options?.clubId);
  if (resolvedClubId) {
    url.searchParams.set('clubId', resolvedClubId);
  }

  const response = await fetch(url, {
    headers: contextAwareHeaders({ Authorization: `Bearer ${token}` }),
  });

  return handleResponse(response, 'Unable to load tournament matches.');
};

export const fetchTournamentLeaderboard = async (tournamentId, token, organizationId, options = {}) => {
  const url = createRequestUrl(`/api/tournaments/${tournamentId}/leaderboard`);

  const resolvedOrganizationId = normalizeId(organizationId);
  if (resolvedOrganizationId) {
    url.searchParams.set('organizationId', resolvedOrganizationId);
  }
  const resolvedCommunityId = normalizeId(options?.communityId);
  if (resolvedCommunityId) {
    url.searchParams.set('communityId', resolvedCommunityId);
  }
  const resolvedClubId = normalizeId(options?.clubId);
  if (resolvedClubId) {
    url.searchParams.set('clubId', resolvedClubId);
  }

  const response = await fetch(url, {
    headers: contextAwareHeaders({ Authorization: `Bearer ${token}` }),
  });

  return handleResponse(response, 'Unable to load tournament leaderboard.');
};

export const fetchPublicTournamentLeaderboard = async (tournamentId) => {
  const response = await fetch(`${API_BASE}/api/tournaments/public/${tournamentId}/leaderboard`);

  return handleResponse(response, 'Unable to load tournament leaderboard.');
};

export const fetchPublicTournamentOnlineUsers = async (tournamentId, viewerId) => {
  const response = await fetch(`${API_BASE}/api/tournaments/public/${tournamentId}/online-users`, {
    method: 'POST',
    cache: 'no-store',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ viewerId }),
  });

  return handleResponse(response, 'Unable to load online viewers.');
};

export const fetchPublicLiveAuctionView = async (tournamentId) => {
  const response = await fetch(`${API_BASE}/api/public/tournaments/${tournamentId}/live-auction`, {
    cache: 'no-store',
  });

  return handleResponse(response, 'Unable to load public live auction view.');
};


export const fetchPublicOnlineAuctionView = async (tournamentId) => {
  const response = await fetch(`${API_BASE}/api/public/auction/${encodeURIComponent(tournamentId)}`, {
    cache: 'no-store',
  });

  return handleResponse(response, 'Unable to load public online auction view.');
};

export const fetchAuctionTournamentPlayers = async (tournamentId, token) => {
  const response = await fetch(`${API_BASE}/api/tournaments/${tournamentId}/auction/players`, {
    headers: contextAwareHeaders({ Authorization: `Bearer ${token}` }),
  });

  return handleResponse(response, 'Unable to load tournament players.');
};

export const fetchAuctionTeamPlayers = async (tournamentId, token) => {
  const response = await fetch(`${API_BASE}/api/tournaments/${tournamentId}/auction/team-players`, {
    headers: contextAwareHeaders({ Authorization: `Bearer ${token}` }),
  });

  return handleResponse(response, 'Unable to load tournament players.');
};

export const fetchLiveAuctionTournamentPlayers = async (tournamentId, token) => {
  const response = await fetch(`${API_BASE}/api/tournaments/${tournamentId}/live-auction/players`, {
    headers: contextAwareHeaders({ Authorization: `Bearer ${token}` }),
  });

  return handleResponse(response, 'Unable to load live auction tournament players.');
};

export const fetchLiveAuctionTeamPlayers = async (tournamentId, token) => {
  const response = await fetch(`${API_BASE}/api/tournaments/${tournamentId}/live-auction/team-players`, {
    headers: contextAwareHeaders({ Authorization: `Bearer ${token}` }),
  });

  return handleResponse(response, 'Unable to load live auction tournament players.');
};

export const updateTournamentPlayerBattingStyle = async (
  tournamentId,
  playerId,
  battingStyle,
  token
) => {
  const response = await fetch(
    `${API_BASE}/api/tournaments/${tournamentId}/players/${playerId}/batting-style`,
    {
      method: 'PATCH',
      headers: contextAwareHeaders({ Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }),
      body: JSON.stringify({ battingStyle }),
    }
  );

  return handleResponse(response, 'Unable to update batting style.');
};

export const fetchAuctionShuffle = async (tournamentId, token) => {
  const response = await fetch(`${API_BASE}/api/tournaments/${tournamentId}/auction/shuffle`, {
    headers: contextAwareHeaders({ Authorization: `Bearer ${token}` }),
  });

  return handleResponse(response, 'Unable to load shuffle status.');
};

export const fetchLiveAuctionShuffle = async (tournamentId, token) => {
  const response = await fetch(`${API_BASE}/api/tournaments/${tournamentId}/live-auction/shuffle`, {
    headers: contextAwareHeaders({ Authorization: `Bearer ${token}` }),
  });

  return handleResponse(response, 'Unable to load live auction shuffle status.');
};

export const fetchAuctionLiveState = async (tournamentId, token) => {
  const response = await fetch(`${API_BASE}/api/tournaments/${tournamentId}/auction/live`, {
    cache: 'no-store',
    headers: contextAwareHeaders({ Authorization: `Bearer ${token}` }),
  });

  return handleResponse(response, 'Unable to load live auction status.');
};

export const fetchLiveAuctionLiveState = async (tournamentId, token) => {
  const response = await fetch(`${API_BASE}/api/tournaments/${tournamentId}/live-auction/live`, {
    cache: 'no-store',
    headers: contextAwareHeaders({ Authorization: `Bearer ${token}` }),
  });

  return handleResponse(response, 'Unable to load live auction status.');
};

const liveAuctionStudioRequest = async (tournamentId, path, token, options = {}) => {
  const response = await fetch(`${API_BASE}/api/tournaments/${tournamentId}/live-auction${path}`, {
    cache: 'no-store',
    ...options,
    headers: contextAwareHeaders({
      ...(options.body ? { 'Content-Type': 'application/json' } : {}),
      Authorization: `Bearer ${token}`,
      ...(options.headers || {}),
    }),
  });
  return handleResponse(response, 'Unable to update the Live Auction operator console.');
};

export const fetchLiveAuctionStudio = (tournamentId, token) => liveAuctionStudioRequest(tournamentId, '/studio', token);
export const fetchLiveAuctionDirector = (tournamentId, token) => liveAuctionStudioRequest(tournamentId, '/director', token);
export const fetchLiveAuctionStreamPreflight = (tournamentId, token) => liveAuctionStudioRequest(tournamentId, '/streaming/preflight', token);
export const updateLiveAuctionStudioScene = (tournamentId, target, payload, token) => liveAuctionStudioRequest(tournamentId, `/studio/${target}`, token, { method: 'POST', body: JSON.stringify(payload) });
export const issueLiveAuctionOverlayToken = (tournamentId, token) => liveAuctionStudioRequest(tournamentId, '/studio/overlay-token', token, { method: 'POST', body: JSON.stringify({}) });
export const revokeLiveAuctionOverlayToken = (tournamentId, token) => liveAuctionStudioRequest(tournamentId, '/studio/overlay-token', token, { method: 'DELETE' });
export const updateLiveAuctionDirector = (tournamentId, payload, token) => liveAuctionStudioRequest(tournamentId, '/director', token, { method: 'PATCH', body: JSON.stringify(payload) });
export const retryLiveAuctionDirectorEvent = (tournamentId, eventId, token) => liveAuctionStudioRequest(tournamentId, `/director/events/${encodeURIComponent(eventId)}/retry`, token, { method: 'POST', body: JSON.stringify({}) });
export const pauseLiveAuctionLiveState = (tournamentId, payload, token) => liveAuctionStudioRequest(tournamentId, '/live/pause', token, { method: 'POST', body: JSON.stringify(payload) });
export const resumeLiveAuctionLiveState = (tournamentId, payload, token) => liveAuctionStudioRequest(tournamentId, '/live/resume', token, { method: 'POST', body: JSON.stringify(payload) });

export const resolveAuctionLiveState = async (tournamentId, token) => {
  const response = await fetch(`${API_BASE}/api/tournaments/${tournamentId}/auction/live/resolve`, {
    method: 'POST',
    headers: contextAwareHeaders({ Authorization: `Bearer ${token}` }),
  });

  return handleResponse(response, 'Unable to resolve live auction status.');
};

export const resolveLiveAuctionLiveState = async (tournamentId, token) => {
  const response = await fetch(`${API_BASE}/api/tournaments/${tournamentId}/live-auction/live/resolve`, {
    method: 'POST',
    headers: contextAwareHeaders({ Authorization: `Bearer ${token}` }),
  });

  return handleResponse(response, 'Unable to resolve live auction status.');
};

export const updateAuctionLivePlayer = async (tournamentId, currentPlayer, token) => {
  const response = await fetch(`${API_BASE}/api/tournaments/${tournamentId}/auction/live/player`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ currentPlayer }),
  });

  return handleResponse(response, 'Unable to update live player.');
};

export const updateLiveAuctionLivePlayer = async (tournamentId, currentPlayer, token) => {
  const response = await fetch(`${API_BASE}/api/tournaments/${tournamentId}/live-auction/live/player`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ currentPlayer }),
  });

  return handleResponse(response, 'Unable to update live auction player.');
};

export const submitAuctionLiveBid = async (tournamentId, payload, token) => {
  const response = await fetch(`${API_BASE}/api/tournaments/${tournamentId}/auction/live/bid`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });

  return handleResponse(response, 'Unable to submit live bid.');
};

export const submitLiveAuctionLiveBid = async (tournamentId, payload, token) => {
  const response = await fetch(`${API_BASE}/api/tournaments/${tournamentId}/live-auction/live/bid`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });

  return handleResponse(response, 'Unable to submit live auction bid.');
};

export const fetchTeamOwnerView = async (tournamentId, token) => {
  const response = await fetch(`${API_BASE}/api/tournaments/${tournamentId}/auction/team-owner`, {
    cache: 'no-store',
    headers: contextAwareHeaders({ Authorization: `Bearer ${token}` }),
  });

  return handleResponse(response, 'Unable to load team owner data.');
};

export const fetchLiveTeamOwnerView = async (tournamentId, token) => {
  const response = await fetch(`${API_BASE}/api/tournaments/${tournamentId}/live-auction/team-owner`, {
    cache: 'no-store',
    headers: contextAwareHeaders({ Authorization: `Bearer ${token}` }),
  });

  return handleResponse(response, 'Unable to load live team owner data.');
};

export const startAuctionShuffle = async (tournamentId, token) => {
  const response = await fetch(`${API_BASE}/api/tournaments/${tournamentId}/auction/shuffle/start`, {
    method: 'POST',
    headers: contextAwareHeaders({ Authorization: `Bearer ${token}` }),
  });

  return handleResponse(response, 'Unable to start the shuffle.');
};

export const startLiveAuctionShuffle = async (tournamentId, token) => {
  const response = await fetch(`${API_BASE}/api/tournaments/${tournamentId}/live-auction/shuffle/start`, {
    method: 'POST',
    headers: contextAwareHeaders({ Authorization: `Bearer ${token}` }),
  });

  return handleResponse(response, 'Unable to start the live auction shuffle.');
};

export const revealAuctionShufflePlayer = async (tournamentId, token) => {
  const response = await fetch(`${API_BASE}/api/tournaments/${tournamentId}/auction/shuffle/reveal`, {
    method: 'POST',
    headers: contextAwareHeaders({ Authorization: `Bearer ${token}` }),
  });

  return handleResponse(response, 'Unable to show the next player.');
};

export const revealLiveAuctionShufflePlayer = async (tournamentId, token) => {
  const response = await fetch(`${API_BASE}/api/tournaments/${tournamentId}/live-auction/shuffle/reveal`, {
    method: 'POST',
    headers: contextAwareHeaders({ Authorization: `Bearer ${token}` }),
  });

  return handleResponse(response, 'Unable to show the next live auction player.');
};

export const advanceAuctionShuffle = async (tournamentId, outcome, token, saleDetails = {}) => {
  const response = await fetch(`${API_BASE}/api/tournaments/${tournamentId}/auction/shuffle/advance`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ outcome, ...saleDetails }),
  });

  return handleResponse(response, 'Unable to update the shuffle.');
};

export const advanceLiveAuctionShuffle = async (tournamentId, outcome, token, saleDetails = {}) => {
  const response = await fetch(`${API_BASE}/api/tournaments/${tournamentId}/live-auction/shuffle/advance`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ outcome, ...saleDetails }),
  });

  return handleResponse(response, 'Unable to update the live auction shuffle.');
};

export const fetchAuctionTeamSetup = async (tournamentId, token) => {
  const response = await fetch(`${API_BASE}/api/tournaments/${tournamentId}/auction/teams`, {
    headers: contextAwareHeaders({ Authorization: `Bearer ${token}` }),
  });

  return handleResponse(response, 'Unable to load auction team setup.');
};

export const fetchLiveAuctionTeamSetup = async (tournamentId, token) => {
  const response = await fetch(`${API_BASE}/api/tournaments/${tournamentId}/live-auction/teams`, {
    headers: contextAwareHeaders({ Authorization: `Bearer ${token}` }),
  });

  return handleResponse(response, 'Unable to load live auction team setup.');
};

export const saveAuctionTeamSetup = async (tournamentId, payload, token) => {
  const response = await fetch(`${API_BASE}/api/tournaments/${tournamentId}/auction/teams`, {
    method: 'POST',
    headers: contextAwareHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    }),
    body: JSON.stringify(payload),
  });

  return handleResponse(response, 'Unable to save auction team setup.');
};

export const saveLiveAuctionTeamSetup = async (tournamentId, payload, token) => {
  const response = await fetch(`${API_BASE}/api/tournaments/${tournamentId}/live-auction/teams`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });

  return handleResponse(response, 'Unable to save live auction team setup.');
};

export const fetchAuctionSettings = async (tournamentId, token) => {
  const response = await fetch(`${API_BASE}/api/tournaments/${tournamentId}/auction/settings`, {
    headers: contextAwareHeaders({ Authorization: `Bearer ${token}` }),
  });

  return handleResponse(response, 'Unable to load auction settings.');
};

export const fetchAuctionMode = async (tournamentId, token) => {
  const response = await fetch(`${API_BASE}/api/tournaments/${tournamentId}/auction/mode`, {
    headers: contextAwareHeaders({ Authorization: `Bearer ${token}` }),
  });

  return handleResponse(response, 'Unable to load auction mode.');
};

export const fetchLiveAuctionSettings = async (tournamentId, token) => {
  const response = await fetch(`${API_BASE}/api/tournaments/${tournamentId}/live-auction/settings`, {
    headers: contextAwareHeaders({ Authorization: `Bearer ${token}` }),
  });

  return handleResponse(response, 'Unable to load live auction settings.');
};

export const saveAuctionSettings = async (tournamentId, payload, token) => {
  const response = await fetch(`${API_BASE}/api/tournaments/${tournamentId}/auction/settings`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });

  return handleResponse(response, 'Unable to save auction settings.');
};

export const saveAuctionMode = async (tournamentId, payload, token) => {
  const response = await fetch(`${API_BASE}/api/tournaments/${tournamentId}/auction/mode`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });

  return handleResponse(response, 'Unable to save auction mode.');
};

export const resetAuctionForModeChange = async (tournamentId, payload, token) => {
  const response = await fetch(`${API_BASE}/api/tournaments/${tournamentId}/auction/mode/reset`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });

  return handleResponse(response, 'Unable to reset auction mode.');
};

export const saveLiveAuctionSettings = async (tournamentId, payload, token) => {
  const response = await fetch(`${API_BASE}/api/tournaments/${tournamentId}/live-auction/settings`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });

  return handleResponse(response, 'Unable to save live auction settings.');
};

export const generateLiveAuctionYouTubeStream = async (tournamentId, token) => {
  const response = await fetch(`${API_BASE}/api/tournaments/${tournamentId}/live-auction/youtube/generate`, {
    method: 'POST',
    headers: contextAwareHeaders({ Authorization: `Bearer ${token}` }),
  });

  return handleResponse(response, 'Unable to generate live auction YouTube stream details.');
};

export const fetchLiveAuctionRelayStatus = async (tournamentId, token) => {
  const response = await fetch(`${API_BASE}/api/tournaments/${tournamentId}/live-auction/relay/status`, {
    headers: contextAwareHeaders({ Authorization: `Bearer ${token}` }),
  });

  return handleResponse(response, 'Unable to fetch live auction relay status.');
};

export const fetchLiveAuctionStreamStatus = async (tournamentId, token) => {
  const response = await fetch(`${API_BASE}/api/tournaments/${tournamentId}/live-auction/streaming/status`, {
    headers: contextAwareHeaders({ Authorization: `Bearer ${token}` }),
  });
  return handleResponse(response, 'Unable to fetch live auction stream status.');
};

export const takeoverLiveAuctionStreamControl = async (tournamentId, token) => {
  const response = await fetch(`${API_BASE}/api/tournaments/${tournamentId}/live-auction/streaming/takeover`, {
    method: 'POST',
    headers: contextAwareHeaders({ Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }),
    body: JSON.stringify({}),
  });
  return handleResponse(response, 'Unable to take over auction stream control.');
};

export const fetchLiveAuctionStreamEvents = async (tournamentId, token) => {
  const response = await fetch(`${API_BASE}/api/tournaments/${tournamentId}/live-auction/streaming/events`, {
    headers: contextAwareHeaders({ Authorization: `Bearer ${token}` }),
  });
  return handleResponse(response, 'Unable to fetch live auction stream logs.');
};

export const fetchPublicLiveAuctionWatch = async (tournamentId) => {
  const response = await fetch(`${API_BASE}/api/public/tournaments/${tournamentId}/live-auction/watch`);
  return handleResponse(response, 'Unable to load public live auction watch details.');
};

export const uploadLiveAuctionTeamLogo = async (tournamentId, file, token, previousLogoUrl = '') => {
  const formData = new FormData();
  formData.append('logo', await optimizeImageFile(file, 'LOGO'));
  if (previousLogoUrl) {
    formData.append('previousLogoUrl', previousLogoUrl);
  }

  const response = await fetch(`${API_BASE}/api/tournaments/${tournamentId}/live-auction/teams/logo`, {
    method: 'POST',
    headers: contextAwareHeaders({ Authorization: `Bearer ${token}` }),
    body: formData,
  });

  return handleResponse(response, 'Unable to upload team logo.');
};

export const uploadMatchTeamLogo = async (tournamentId, file, token, previousLogoUrl = '') => {
  const formData = new FormData();
  formData.append('logo', await optimizeImageFile(file, 'LOGO'));
  if (previousLogoUrl) {
    formData.append('previousLogoUrl', previousLogoUrl);
  }

  const response = await fetch(`${API_BASE}/api/tournaments/${tournamentId}/matches/team-logo`, {
    method: 'POST',
    headers: contextAwareHeaders({ Authorization: `Bearer ${token}` }),
    body: formData,
  });

  return handleResponse(response, 'Unable to upload team logo.');
};

export const createTeamOwnerAdmin = async (tournamentId, payload, token) => {
  const response = await fetch(`${API_BASE}/api/tournaments/${tournamentId}/auction/team-owner-admins`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });

  return handleResponse(response, 'Unable to create a team owner.');
};


const DEPRECATED_TOURNAMENT_PAYLOAD_FIELDS = new Set([
  'category',
  'tournamentCategory',
  'needMoreTeams',
  'needOfficials',
  'registrationMode',
]);

const TOURNAMENT_DATE_PAYLOAD_FIELDS = new Set(['tournamentStartDate', 'tournamentEndDate', 'registrationEndDate']);

const toIsoDatePayloadValue = (value) => {
  if (!value) return value;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toISOString();
};

const sanitizeTournamentPayload = (payload = {}) => Object.entries(payload || {}).reduce((acc, [key, value]) => {
  if (DEPRECATED_TOURNAMENT_PAYLOAD_FIELDS.has(key)) return acc;
  acc[key] = TOURNAMENT_DATE_PAYLOAD_FIELDS.has(key) ? toIsoDatePayloadValue(value) : value;
  return acc;
}, {});

export const createTournament = async (payload, token) => {
  const formData = new FormData();
  for (const [key, value] of Object.entries(sanitizeTournamentPayload(payload))) {
    if (value === undefined || value === null) continue;
    if ((key === 'tournamentLogo' || key === 'tournamentBanner') && value instanceof File) {
      formData.append(key, await optimizeImageFile(value, key === 'tournamentLogo' ? 'LOGO' : 'BANNER'));
      continue;
    }
    formData.append(key, typeof value === 'object' ? JSON.stringify(value) : String(value));
  }
  const response = await fetch(`${API_BASE}/api/tournaments`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });

  return handleResponse(response, 'Unable to create tournament.');
};

export const updateTournament = async (tournamentId, payload, token) => {
  const formData = new FormData();
  for (const [key, value] of Object.entries(sanitizeTournamentPayload(payload))) {
    if (value === undefined || value === null) continue;
    if ((key === 'tournamentLogo' || key === 'tournamentBanner') && value instanceof File) {
      formData.append(key, await optimizeImageFile(value, key === 'tournamentLogo' ? 'LOGO' : 'BANNER'));
      continue;
    }
    formData.append(key, typeof value === 'object' ? JSON.stringify(value) : String(value));
  }
  const response = await fetch(`${API_BASE}/api/tournaments/${tournamentId}`, {
    method: 'PATCH',
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });

  return handleResponse(response, 'Unable to update tournament.');
};

export const closeTournamentRegistration = async (tournamentId, token) => {
  const response = await fetch(`${API_BASE}/api/tournaments/${tournamentId}/registration/close`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
  });

  return handleResponse(response, 'Unable to close registration.');
};

export const deleteTournament = async (tournamentId, token) => {
  const response = await fetch(`${API_BASE}/api/tournaments/${tournamentId}`, {
    method: 'DELETE',
    headers: contextAwareHeaders({ Authorization: `Bearer ${token}` }),
  });

  return handleResponse(response, 'Unable to delete tournament.');
};

export const activateTournament = async (tournamentId, token) => {
  const response = await fetch(`${API_BASE}/api/tournaments/${tournamentId}/activate`, {
    method: 'PATCH',
    headers: contextAwareHeaders({ Authorization: `Bearer ${token}` }),
  });

  return handleResponse(response, 'Unable to activate tournament.');
};

export const updateTournamentAdmins = async (
  tournamentId,
  adminIds,
  token,
  managers = {},
  scope = {}
) => {
  const response = await fetch(`${API_BASE}/api/tournaments/${tournamentId}/admins`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      adminIds,
      registrationManagerId: managers.registrationManagerId,
      auctionManagerId: managers.auctionManagerId,
      matchManagerId: managers.matchManagerId,
      organizationId: scope.organizationId,
      communityId: scope.communityId,
    }),
  });

  return handleResponse(response, 'Unable to update tournament admins.');
};

export const fetchEligibleTournamentPlayers = async (tournamentId, token, scope = {}) => {
  const url = createRequestUrl(`/api/tournaments/${tournamentId}/eligible-players`);

  const resolvedOrganizationId = normalizeId(scope?.organizationId);
  if (resolvedOrganizationId) {
    url.searchParams.set('organizationId', resolvedOrganizationId);
  }
  const resolvedCommunityId = normalizeId(scope?.communityId);
  if (resolvedCommunityId) {
    url.searchParams.set('communityId', resolvedCommunityId);
  }
  const resolvedClubId = normalizeId(scope?.clubId);
  if (resolvedClubId) {
    url.searchParams.set('clubId', resolvedClubId);
  }

  const response = await fetch(url.toString(), {
    headers: contextAwareHeaders({ Authorization: `Bearer ${token}` }),
  });

  return handleResponse(response, 'Unable to load players for this tournament.');
};

export const removeTournamentPlayer = async (tournamentId, playerId, token) => {
  const response = await fetch(`${API_BASE}/api/tournaments/${tournamentId}/players/${playerId}`, {
    method: 'DELETE',
    headers: contextAwareHeaders({ Authorization: `Bearer ${token}` }),
  });

  return handleResponse(response, 'Unable to remove player from this tournament.');
};

export const addTournamentPlayers = async (tournamentId, playerIds, token, scope = {}) => {
  const payload = { playerIds };
  const organizationId = normalizeId(scope?.organizationId);
  const communityId = normalizeId(scope?.communityId);
  const clubId = normalizeId(scope?.clubId);

  if (organizationId) payload.organizationId = organizationId;
  if (communityId) payload.communityId = communityId;
  if (clubId) payload.clubId = clubId;

  const response = await fetch(`${API_BASE}/api/tournaments/${tournamentId}/players`, {
    method: 'POST',
    headers: contextAwareHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    }),
    body: JSON.stringify(payload),
  });

  return handleResponse(response, 'Unable to add players to this tournament.');
};

export const fetchTournamentJoinLink = async (tournamentId, token) => {
  const response = await fetch(`${API_BASE}/api/tournaments/${tournamentId}/join-link`, {
    headers: contextAwareHeaders({ Authorization: `Bearer ${token}` }),
  });
  return handleResponse(response, 'Unable to fetch join link.');
};

export const getTournamentJoinLink = async (tournamentId, token) => {
  const data = await fetchTournamentJoinLink(tournamentId, token);
  return {
    ...data,
    link: data?.link || data?.joinUrl || data?.tournamentJoinLink?.link || '',
    joinUrl: data?.joinUrl || data?.link || '',
  };
};

export const fetchPublicTournamentJoinContext = async (inviteCode, token = null) => {
  const response = await fetch(`${API_BASE}/api/tournaments/public/join/${inviteCode}`, {
    headers: token ? contextAwareHeaders({ Authorization: `Bearer ${token}` }) : undefined,
  });
  return handleResponse(response, 'Unable to load tournament join details.');
};

export const joinPublicTournament = async (inviteCode, token) => {
  const response = await fetch(`${API_BASE}/api/tournaments/public/join/${inviteCode}`, {
    method: 'POST',
    headers: contextAwareHeaders({ Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }),
    body: JSON.stringify({}),
  });
  return handleResponse(response, 'Unable to join tournament.');
};

export const requestPublicTournamentJoinOtp = async (inviteCode, payload) => {
  const response = await fetch(`${API_BASE}/api/public/tournaments/join/${inviteCode}/request-otp`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return handleResponse(response, 'Unable to request OTP.');
};

export const verifyPublicTournamentJoinOtp = async (inviteCode, payload) => {
  const response = await fetch(`${API_BASE}/api/public/tournaments/join/${inviteCode}/verify-otp`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return handleResponse(response, 'Unable to verify OTP.');
};

export const inviteTournamentPlayers = async (tournamentId, playerIds, token) => {
  const response = await fetch(`${API_BASE}/api/tournaments/${tournamentId}/invites`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ playerIds }),
  });

  return handleResponse(response, 'Unable to invite players to this tournament.');
};

export const fetchPlayerCategories = async (tournamentId, token) => {
  const response = await fetch(`${API_BASE}/api/tournaments/${tournamentId}/player-categories`, {
    headers: contextAwareHeaders({ Authorization: `Bearer ${token}` }),
  });

  return handleResponse(response, 'Unable to load player categories.');
};

export const createPlayerCategory = async (tournamentId, name, token) => {
  const response = await fetch(`${API_BASE}/api/tournaments/${tournamentId}/player-categories`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ name }),
  });

  return handleResponse(response, 'Unable to create category.');
};

export const updatePlayerCategory = async (tournamentId, categoryId, name, token) => {
  const response = await fetch(
    `${API_BASE}/api/tournaments/${tournamentId}/player-categories/${categoryId}`,
    {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ name }),
    },
  );

  return handleResponse(response, 'Unable to update category.');
};

export const deletePlayerCategory = async (tournamentId, categoryId, token) => {
  const response = await fetch(
    `${API_BASE}/api/tournaments/${tournamentId}/player-categories/${categoryId}`,
    {
      method: 'DELETE',
      headers: contextAwareHeaders({ Authorization: `Bearer ${token}` }),
    },
  );

  return handleResponse(response, 'Unable to delete category.');
};

const normalizeIds = (values = []) => values.map((value) => value?.toString?.() || `${value}`);

export const assignPlayersToCategory = async (tournamentId, playerIds, categoryId, token) => {
  const normalizedPlayerIds = normalizeIds(playerIds);

  const response = await fetch(`${API_BASE}/api/tournaments/${tournamentId}/player-categories/assign`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ playerIds: normalizedPlayerIds, categoryId }),
  });

  return handleResponse(response, 'Unable to update player categorization.');
};

export const fetchSelectedPlayerCategories = async (tournamentId, playerIds, token) => {
  const response = await fetch(
    `${API_BASE}/api/tournaments/${tournamentId}/player-categories/categorize`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ playerIds }),
    },
  );

  return handleResponse(response, 'Unable to categorize selected players.');
};


export const createTournamentJoinLink = async (tournamentId, token, payload = {}) => {
  const response = await fetch(`${API_BASE}/api/tournaments/${tournamentId}/join-link`, {
    method: 'POST',
    headers: contextAwareHeaders({ Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }),
    body: JSON.stringify(payload),
  });
  const data = await handleResponse(response, 'Unable to create join link.');

  return {
    ...data,
    link: data?.link || data?.tournamentJoinLink?.link || data?.joinLink || '',
    token: data?.token || data?.tournamentJoinLink?.token || '',
    expiresAt: data?.expiresAt || data?.tournamentJoinLink?.expiresAt || null,
    maxUses: data?.maxUses ?? data?.tournamentJoinLink?.maxUses ?? null,
    usedCount: data?.usedCount ?? data?.tournamentJoinLink?.usedCount ?? 0,
  };
};
export const getPublicTournamentJoinInvite = async (inviteToken) => handleResponse(await fetch(`${API_BASE}/api/tournaments/public/tournament-join/${encodeURIComponent(inviteToken)}`), 'Unable to load join invite.');
export const requestTournamentJoinOtp = async ({ inviteToken, email, mobile }) => handleResponse(await fetch(`${API_BASE}/api/tournaments/public/tournament-join/${encodeURIComponent(inviteToken)}/request-otp`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email, mobile }) }), 'Unable to request OTP.');
export const verifyTournamentJoinOtp = async ({ inviteToken, email, mobile, otp, tournamentRoles }) => handleResponse(await fetch(`${API_BASE}/api/tournaments/public/tournament-join/${encodeURIComponent(inviteToken)}/verify`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email, mobile, otp, tournamentRoles }) }), 'Unable to verify OTP.');
